import { ISelectOption, IRenderFormField } from "src/@types/form";
import { MEDIA_ACCEPT, IMAGE_ACCEPT } from "src/components/FormField/constants";
import { EditorField } from "src/components/FormField/EditorField";
import { MinMaxNumberField } from "src/components/FormField/MinMaxNumberField";
import { MultiButtonSelectorField } from "src/components/FormField/MultiButtonSelectorField";
import { MultiDocField } from "src/components/FormField/MultiDocField";
import { SingleButtonSelectorField } from "src/components/FormField/SingleButtonSelectorField";
import { SingleSelectField } from "src/components/FormField/SingleSelectField";
import { TextField } from "src/components/FormField/TextField";
import { formFieldTrns } from "src/components/FormField/trns";
import { MultiSelectField } from "src/components/FormField/MultiSelectField";
import { CloneImagesField } from "../CloneImagesField";
import {
  EMPLOYEE_COUNT_OPTIONS,
  MARKET_AGE_OPTIONS,
  CUSTOMER_COUNT_OPTIONS,
  YES_NO_OPTIONS,
} from "../temp/constants";
import { CloneFields } from "../types";

const DEFAULT_ERROR_CLASSNAME = "absolute left-0 -bottom-5 sm:-bottom-6";

export const getCloneEditCreateFields = (
  clonTypeOptions: ISelectOption[],
  industryOptions: ISelectOption[],
  soldInformationOptions: ISelectOption[],
  countriesOptions: ISelectOption[]
): IRenderFormField[] => [
  {
    component: SingleButtonSelectorField,
    name: CloneFields.cloneType,
    className: "flex flex-col sm:flex-row",
    selectorClassName: "!justify-start px-5 py-3",
    options: clonTypeOptions,
  },
  {
    component: TextField,
    name: CloneFields.companyName,
    label: { i18nKey: formFieldTrns.companyName },
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: MultiDocField,
    name: CloneFields.companyPresentation,
    label: {
      i18nKey: formFieldTrns.companyPresentation,
    },
    className: "!text-base sm:!text-xl",
    placeholder: {
      i18nKey: formFieldTrns.companyPresentationPlaceholder,
    },
    accept: MEDIA_ACCEPT,
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: TextField,
    name: CloneFields.googleProfile,
    placeholder: "https://maps.app.goo.gl/zLDRL2dX52eHCjgu3",
    label: {
      i18nKey: formFieldTrns.googleBusinessProfileLink,
    },
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: SingleSelectField,
    name: CloneFields.industry,
    label: {
      i18nKey: formFieldTrns.industry,
    },
    options: industryOptions,
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: TextField,
    name: CloneFields.website,
    label: {
      i18nKey: formFieldTrns.websiteLink,
    },
    placeholder: "https://www.businessclone.com",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: EditorField,
    name: CloneFields.describeProduct,
    label: { i18nKey: formFieldTrns.describeYourProduct },
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: TextField,
    name: CloneFields.advantages,
    label: { i18nKey: formFieldTrns.mainAdvantages },
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: TextField,
    name: CloneFields.investmentForStart,
    labelClassName: "text-wrap",
    label: { i18nKey: formFieldTrns.amountOfNecessaryInvestment },
    type: "number",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: SingleButtonSelectorField,
    name: CloneFields.employees,
    label: { i18nKey: formFieldTrns.numberOfEmployees },
    className: "flex-1 justify-end !gap-2 w-full sm:!gap-4",
    fieldClassName: "flex sm:!flex-row sm:items-center justify-between",
    selectorClassName: "max-w-67 w-full px-5 py-3",
    options: EMPLOYEE_COUNT_OPTIONS,
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: SingleButtonSelectorField,
    name: CloneFields.yearsOnMarket,
    label: { i18nKey: formFieldTrns.howLongOnMarket },
    className: "flex-1 justify-end !gap-2 w-full sm:!gap-4",
    fieldClassName: "flex sm:!flex-row sm:items-center justify-between",
    selectorClassName: "max-w-67 w-full px-5 py-3",
    options: MARKET_AGE_OPTIONS,
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: SingleButtonSelectorField,
    name: CloneFields.customersCount,
    label: { i18nKey: formFieldTrns.numberOfCustomers },
    className: "flex-1 justify-end !gap-2 w-full sm:!gap-4",
    fieldClassName: "flex sm:!flex-row sm:items-center justify-between",
    selectorClassName: "max-w-67 w-full px-5 py-3",
    options: CUSTOMER_COUNT_OPTIONS,
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: MinMaxNumberField,
    name: [CloneFields.companyTurnoverMin, CloneFields.companyTurnoverMax],
    label: { i18nKey: formFieldTrns.annualTurnoverOfCompany },
    type: "number",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
    labelClassName: "flex flex-1 w-full capitalize !text-wrap",
    fieldClassName:
      "flex !flex-col !justify-start sm:!flex-row gap-5 sm:items-center",
  },
  {
    component: MinMaxNumberField,
    name: [CloneFields.monthlyIncomeMin, CloneFields.monthlyIncomeMax],
    label: { i18nKey: formFieldTrns.monthlyIncomeOfCompany },
    type: "number",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
    labelClassName: "flex flex-1 w-full capitalize !text-wrap",
    fieldClassName:
      "flex !flex-col !justify-start sm:!flex-row gap-5 sm:items-center",
  },
  {
    component: SingleButtonSelectorField,
    name: CloneFields.readyToAuthorizedCapital,
    label: { i18nKey: formFieldTrns.readyToParticipateOfCloneCapital },
    className: "flex-1 !gap-2 sm:!gap-4",
    labelClassName: "flex flex-1 !text-wrap",
    fieldClassName: "flex sm:!flex-row sm:items-center",
    selectorClassName: "px-5 py-3",
    fieldDescription: {
      i18nKey: formFieldTrns.readyToParticipateOfCloneCapitalDesc,
    },
    fieldDescriptionClassName:
      "bg-purple-lighter rounded-20 p-3 mt-3 font-normal",
    options: YES_NO_OPTIONS,
  },
  {
    component: SingleButtonSelectorField,
    name: CloneFields.patentsBrands,
    label: { i18nKey: formFieldTrns.ownsRightsToPatents },
    className: "flex-1 !gap-2 sm:!gap-4",
    labelClassName: "flex flex-1 !text-wrap",
    fieldClassName: "flex sm:!flex-row sm:items-center",
    selectorClassName: "px-5 py-3",
    options: YES_NO_OPTIONS,
  },
  {
    component: TextField,
    name: CloneFields.cloneCost,
    label: { i18nKey: formFieldTrns.costOfClone },
    type: "number",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: EditorField,
    name: CloneFields.activities,
    label: { i18nKey: formFieldTrns.companysActivities },
    errorClassName: DEFAULT_ERROR_CLASSNAME,
    labelClassName: "text-wrap",
  },
  {
    component: TextField,
    name: CloneFields.companyClients,
    label: { i18nKey: formFieldTrns.companysClients },
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: TextField,
    name: CloneFields.patentsBrandsText,
    label: { i18nKey: formFieldTrns.ownsRightsToPatents },
    labelClassName: "text-wrap",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: TextField,
    name: CloneFields.supportBuyer,
    label: { i18nKey: formFieldTrns.timeCompanyWillSupportBuyer },
    labelClassName: "text-wrap",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
  },
  {
    component: MultiButtonSelectorField,
    name: CloneFields.informationSold,
    label: { i18nKey: formFieldTrns.informationSoldInCloneForm },
    fieldClassName: "sm:!gap-5",
    className: "grid sm:grid-cols-2 lg:grid-cols-3 !gap-2 sm:!gap-5",
    selectorClassName:
      "!justify-start min-h-16 px-5 text-base text-start lowercase",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
    options: soldInformationOptions,
  },
  {
    component: SingleButtonSelectorField,
    name: CloneFields.accessToProduction,
    label: {
      i18nKey:
        formFieldTrns.accessToProductionAndInvolvementBuyersRepresentative,
    },
    className: "flex-1 !gap-2 sm:!gap-4",
    labelClassName: "flex flex-1 !text-wrap ",
    fieldClassName: "flex sm:!flex-row sm:items-center",
    selectorClassName: "px-5 py-3",
    options: YES_NO_OPTIONS,
  },
  {
    component: CloneImagesField,
    name: [CloneFields.logo, CloneFields.mainPhoto],
    label: {
      i18nKey: formFieldTrns.companyBranding,
    },
    labelClassName: "w-full sm:w-auto",
    fieldClassName: "flex !flex-col sm:!flex-row !gap-2 sm:!gap-4",
    className: "!text-base sm:!text-xl",
    errorClassName: DEFAULT_ERROR_CLASSNAME,
    accept: IMAGE_ACCEPT,
  },
  {
    component: MultiSelectField,
    name: CloneFields.prohibitedCountries,
    labelClassName: "text-wrap",
    label: { i18nKey: formFieldTrns.prohibitedCountries },
    options: countriesOptions,
  },
  {
    component: EditorField,
    name: CloneFields.additionalInformation,
    label: { i18nKey: formFieldTrns.additionalInformation },
    labelClassName: "text-wrap",
  },
];
